import { useContext, useEffect, useRef, useState } from "react";
import { submitFeedback, Feedback } from "../../api";
import buttonFeedbackIcon from "../../assets/button_feedback.svg";
import { FeedbackState } from "./FeedbackProvider";
import buttonCloseTransparent from '../../assets/button_close_transparent.svg';
import styles from "./Feedback.module.css";
import { useTexts } from "../../context/TextProvider";

export default function FeedbackButtons({
    messageId,
    traceId,
}: Readonly<{ messageId: string; traceId?: string }>) {
    const feedbackContext = useContext(FeedbackState);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [comment, setComment] = useState("");
    const { texts } = useTexts();

    const modalRef = useRef<HTMLDivElement>(null);
    const feedbackModal = texts?.texts.chat.feedbackModal;

    useEffect(() => {
        if (feedbackContext?.feedback.has(messageId)) {
            setIsSubmitted(true);
        }
    }, [feedbackContext, messageId]);

    useEffect(() => {
        if (!isModalOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsModalOpen(false);
        };
        document.addEventListener("keydown", handleKeyDown);
        modalRef.current?.focus({ preventScroll: true });
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isModalOpen]);

    const toggleOption = (option: string) => {
        setSelectedOptions((prev) =>
            prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
        );
    };

    const handleSubmit = async () => {
        const newFeedback: Feedback = {
            messageId,
            reasons: selectedOptions,
            comment: comment.trim() || undefined,
            traceId,
        };
        await submitFeedback(newFeedback);
        if (feedbackContext) {
            feedbackContext.setFeedback((prev) => {
                const updated = new Map(prev);
                updated.set(messageId, newFeedback);
                return updated;
            });
        }
        setIsSubmitted(true);
        setIsModalOpen(false);
    };

    const isSubmitDisabled = selectedOptions.length === 0 && comment.trim() === "";

    return (
        <>
            <button
                className={styles.feedbackBtn}
                onClick={() => !isSubmitted && setIsModalOpen(true)}
                aria-label={texts?.texts.chat.buttons.feedbackButton ?? "Feedback"}
                disabled={isSubmitted}
            >
                <img src={buttonFeedbackIcon} alt="" />
                <span className={styles.feedbackBtnLabel}>
                    {texts?.texts.chat.buttons.feedbackButton ?? "Feedback"}
                </span>
            </button>

            {isModalOpen && (
                <div
                    className={styles.modalOverlay}
                    onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}
                    aria-modal="true"
                    role="dialog"
                    aria-labelledby="feedback-modal-title"
                >
                    <div className={styles.modalCard} ref={modalRef} tabIndex={-1}>
                        <div className={styles.modalHeader}>
                            <span id="feedback-modal-title" className={styles.modalTitle}>
                                {feedbackModal?.title ?? "Was stimmt aus Ihrer Sicht nicht?"}
                            </span>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setIsModalOpen(false)}
                                aria-label="Feedback-Formular schließen"
                            >
                                <img
                                    src={buttonCloseTransparent}
                                    alt=""
                                    aria-hidden="true"
                                    width={14}
                                    height={14}
                                />
                            </button>
                        </div>

                        <ul className={styles.optionsList}>
                            {(feedbackModal?.options ?? []).map((option) => (
                                <li key={option}>
                                    <label className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={selectedOptions.includes(option)}
                                            onChange={() => toggleOption(option)}
                                        />
                                        <span>{option}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>

                        <div className={styles.detailsSection}>
                            <div className={styles.detailsLabelWrapper}>
                                <span className={styles.detailsLabel}>
                                    {feedbackModal?.detailsLabel ?? "Möchten Sie uns mehr Details geben?"}
                                </span>
                                <span className={styles.detailsOptional}>
                                    {" "}{feedbackModal?.detailsOptional ?? "(optional)"}
                                </span>
                            </div>
                            <textarea
                                className={styles.detailsTextarea}
                                placeholder={feedbackModal?.placeholder ?? "Schreiben Sie etwas ..."}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={3}
                            />
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => setIsModalOpen(false)}
                            >
                                {feedbackModal?.cancelButton ?? "Abbrechen"}
                            </button>
                            <button
                                className={styles.sendBtn}
                                onClick={handleSubmit}
                                disabled={isSubmitDisabled}
                            >
                                {feedbackModal?.sendButton ?? "Senden"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
